'use memo';

import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Suspense } from 'react';

import { NotFound } from '@/components/Error/NotFound';
import { VStack } from '@/components/Layout';

import {
  MY_PAY_SLIP_EMPLOYEE_ID,
  useMyPaySlipEmployeeId,
} from '../-hooks/useMyEmployeeId';

import { EmployeePaySlipDetailHeader } from './-components/EmployeePaySlipDetailHeader';
import { useEmployeePaySlipProfile } from './-query';
import { getEmployeeLatestPaySlipPeriodFromDb } from './-shared/parent-api';
import { EmployeePaySlipDetailSearchSchema } from './-shared/zod-schema';

export const Route = createFileRoute(
  '/_individual/individual/portal/lookup/pay-slip',
)({
  validateSearch: zodValidator(EmployeePaySlipDetailSearchSchema),
  beforeLoad: async ({ search }) => {
    const y = search.year;
    const m = search.month;
    if (y != null && m != null) return;

    const latest = await getEmployeeLatestPaySlipPeriodFromDb(
      MY_PAY_SLIP_EMPLOYEE_ID,
    );
    if (!latest) return;

    throw redirect({
      to: '/individual/portal/lookup/pay-slip',
      search: { year: latest.year, month: latest.month },
      replace: true,
    });
  },
  component: EmployeePaySlipEmployeeDetailShell,
});

function EmployeePaySlipEmployeeDetailShell() {
  const employeeId = useMyPaySlipEmployeeId();
  const { data: profile, isPending } = useEmployeePaySlipProfile(employeeId);

  if (isPending) {
    return (
      <VStack className="h-full min-h-0 min-w-0 flex-1">
        <div className="border-b border-neutral-muted-border">
          <VStack className="gap-3 bg-neutral-fill px-12 pt-4 shadow-sm">
            <div className="h-4 w-64 animate-pulse rounded bg-neutral-muted-fill" />
            <div className="h-10 w-96 animate-pulse rounded bg-neutral-muted-fill" />
            <div className="h-9 w-full max-w-2xl animate-pulse rounded bg-neutral-muted-fill" />
          </VStack>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-auto px-12 pt-6">
          <div className="h-48 w-full animate-pulse rounded-lg bg-neutral-muted-fill" />
        </div>
      </VStack>
    );
  }

  if (!profile) {
    return <NotFound />;
  }

  return (
    <VStack className="h-full min-h-0 min-w-0 flex-1">
      <EmployeePaySlipDetailHeader profile={profile} />
      <Suspense
        fallback={
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto px-12 py-6">
            <div className="h-48 w-full animate-pulse rounded-lg bg-neutral-muted-fill" />
          </div>
        }
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto bg-neutral-bottom-fill">
          <Outlet />
        </div>
      </Suspense>
    </VStack>
  );
}
